import React, { useState, useRef, useEffect } from 'react';
import { 
  FileText, 
  Upload, 
  Download, 
  ShieldCheck, 
  Lock, 
  Info, 
  RefreshCw, 
  Eye, 
  Sliders,
  CheckCircle2
} from 'lucide-react';

export const IdWatermarkPage: React.FC = () => {
  const [recipientCompany, setRecipientCompany] = useState('Acme Real Estate Agencies');
  const [purpose, setPurpose] = useState('Identity verification for tenancy application only');
  const [watermarkDate, setWatermarkDate] = useState('20 AUGUST 2026');
  const [customText, setCustomText] = useState('COPY — FOR IDENTITY VERIFICATION ONLY');
  const [opacity, setOpacity] = useState(0.45);
  const [fontSize, setFontSize] = useState(26);
  const [watermarkStyle, setWatermarkStyle] = useState<'diagonal_grid' | 'centre_stamp' | 'footer_banner'>('diagonal_grid');
  const [colorTheme, setColorTheme] = useState<'red' | 'blue' | 'black'>('red');
  
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Generate synthetic sample ID image for instant preview if no user file uploaded
  useEffect(() => {
    const sampleCanvas = document.createElement('canvas');
    sampleCanvas.width = 800;
    sampleCanvas.height = 500;
    const ctx = sampleCanvas.getContext('2d');
    if (ctx) {
      // Card background
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(0, 0, 800, 500);
      
      // Card header bar
      ctx.fillStyle = '#059669';
      ctx.fillRect(0, 0, 800, 80);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 24px sans-serif';
      ctx.fillText('AUSTRALIA — SAMPLE DRIVER LICENCE', 30, 50);

      // Photo placeholder
      ctx.fillStyle = '#334155';
      ctx.fillRect(40, 110, 160, 200);
      ctx.fillStyle = '#94a3b8';
      ctx.font = '14px sans-serif';
      ctx.fillText('SAMPLE PHOTO', 70, 210);

      // Details
      ctx.fillStyle = '#cbd5e1';
      ctx.font = '16px sans-serif';
      ctx.fillText('NAME: CITIZEN, ALEX JOHN', 230, 140);
      ctx.fillText('DOB: 14/04/1988', 230, 180);
      ctx.fillText('LICENCE NO: 9812 4401', 230, 220);
      ctx.fillText('CARD NO: 4891 0294', 230, 260);
      ctx.fillText('EXPIRY: 14/04/2029', 230, 300);
      ctx.fillText('ADDRESS: 42 GEORGE ST, SYDNEY NSW 2000', 40, 360);

      setImageSrc(sampleCanvas.toDataURL('image/png'));
    }
  }, []);

  // Re-render canvas with watermark whenever settings change
  useEffect(() => {
    if (!imageSrc || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageSrc;
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw original image
      ctx.drawImage(img, 0, 0);

      // Watermark setup
      const colorMap = {
        red: `rgba(220, 38, 38, ${opacity})`,
        blue: `rgba(37, 99, 235, ${opacity})`,
        black: `rgba(15, 23, 42, ${opacity})`
      };

      const strokeColorMap = {
        red: `rgba(255, 255, 255, ${opacity * 0.7})`,
        blue: `rgba(255, 255, 255, ${opacity * 0.7})`,
        black: `rgba(255, 255, 255, ${opacity * 0.7})`
      };

      const line1 = customText;
      const line2 = `PROVIDED TO: ${recipientCompany.toUpperCase()}`;
      const line3 = `DATE: ${watermarkDate.toUpperCase()} • PURPOSE: ${purpose.toUpperCase()}`;

      if (watermarkStyle === 'diagonal_grid') {
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(-Math.PI / 6); // 30 deg angle

        ctx.font = `bold ${fontSize}px sans-serif`;
        ctx.fillStyle = colorMap[colorTheme];
        ctx.textAlign = 'center';

        const stepY = fontSize * 5;
        const startY = -canvas.height;
        const endY = canvas.height;

        for (let y = startY; y < endY; y += stepY) {
          ctx.fillText(line1, 0, y);
          ctx.font = `bold ${Math.round(fontSize * 0.75)}px sans-serif`;
          ctx.fillText(`${line2} — ${line3}`, 0, y + fontSize + 4);
          ctx.font = `bold ${fontSize}px sans-serif`;
        }
        ctx.restore();
      } else if (watermarkStyle === 'centre_stamp') {
        ctx.save();
        const stampW = canvas.width * 0.75;
        const stampH = 140;
        const stampX = (canvas.width - stampW) / 2;
        const stampY = (canvas.height - stampH) / 2;

        ctx.fillStyle = `rgba(15, 23, 42, ${opacity * 0.85})`;
        ctx.strokeStyle = colorMap[colorTheme];
        ctx.lineWidth = 4;
        ctx.fillRect(stampX, stampY, stampW, stampH);
        ctx.strokeRect(stampX, stampY, stampW, stampH);

        ctx.font = `bold ${fontSize}px sans-serif`;
        ctx.fillStyle = colorMap[colorTheme];
        ctx.textAlign = 'center';
        ctx.fillText(line1, canvas.width / 2, stampY + 40);

        ctx.font = `bold ${Math.round(fontSize * 0.7)}px sans-serif`;
        ctx.fillStyle = '#ffffff';
        ctx.fillText(line2, canvas.width / 2, stampY + 75);
        ctx.fillText(line3, canvas.width / 2, stampY + 110);
        ctx.restore();
      } else {
        // Footer banner
        ctx.save();
        const bannerH = 100;
        const bannerY = canvas.height - bannerH;
        ctx.fillStyle = `rgba(15, 23, 42, ${Math.max(opacity, 0.7)})`;
        ctx.fillRect(0, bannerY, canvas.width, bannerH);

        ctx.font = `bold ${Math.round(fontSize * 0.85)}px sans-serif`;
        ctx.fillStyle = colorMap[colorTheme];
        ctx.textAlign = 'center';
        ctx.fillText(line1, canvas.width / 2, bannerY + 35);

        ctx.font = `bold ${Math.round(fontSize * 0.65)}px sans-serif`;
        ctx.fillStyle = '#ffffff';
        ctx.fillText(`${line2} | ${line3}`, canvas.width / 2, bannerY + 70);
        ctx.restore();
      }
    };
  }, [imageSrc, recipientCompany, purpose, watermarkDate, customText, opacity, fontSize, watermarkStyle, colorTheme]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setImageSrc(event.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDownload = () => {
    if (!canvasRef.current) return;
    const link = document.createElement('a');
    link.download = `watermarked_${recipientCompany.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}.png`;
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-950 text-teal-300 text-xs font-semibold border border-teal-800">
          <Lock className="w-3.5 h-3.5" />
          <span>Client-Side Secure Watermark Generator</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white">
          Watermark Your Identification Documents
        </h1>
        <p className="text-slate-400 text-sm max-w-xl mx-auto">
          Protect copies of your Driver Licence, Passport, and Medicare card before sharing them with real estate agents, car rentals, or employers.
        </p>
      </div>

      {/* Main Workspace Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Form & Customizer */}
        <div className="lg:col-span-5 bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-5 shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
              <Sliders className="w-4 h-4 text-teal-400" />
              <span>Watermark Settings</span>
            </h3>
            <span className="text-[10px] text-emerald-400 font-mono bg-emerald-950 px-2 py-0.5 rounded border border-emerald-800">
              100% In-Browser Privacy
            </span>
          </div>

          {/* Upload File button */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider">
              Upload Your Document / Image:
            </label>
            <label className="flex flex-col items-center justify-center p-4 rounded-xl border-2 border-dashed border-slate-700 hover:border-teal-500 bg-slate-950 cursor-pointer transition-colors text-center text-xs text-slate-400">
              <Upload className="w-6 h-6 text-teal-400 mb-1" />
              <span className="font-semibold text-slate-200">Click to upload or drag & drop</span>
              <span className="text-[10px] text-slate-500">PNG, JPEG, WebP (Images never leave your browser)</span>
              <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
            </label>
          </div>

          {/* Recipient Company */}
          <div className="space-y-1">
            <label className="block text-xs font-semibold text-slate-300">
              Document being provided to (Company Name):
            </label>
            <input
              type="text"
              value={recipientCompany}
              onChange={(e) => setRecipientCompany(e.target.value)}
              placeholder="e.g. Ray White Surry Hills"
              className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white text-xs focus:outline-none focus:border-teal-500"
            />
          </div>

          {/* Purpose */}
          <div className="space-y-1">
            <label className="block text-xs font-semibold text-slate-300">
              Explicit Purpose:
            </label>
            <input
              type="text"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="e.g. Rental application for 10/24 Smith St"
              className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white text-xs focus:outline-none focus:border-teal-500"
            />
          </div>

          {/* Date */}
          <div className="space-y-1">
            <label className="block text-xs font-semibold text-slate-300">
              Date:
            </label>
            <input
              type="text"
              value={watermarkDate}
              onChange={(e) => setWatermarkDate(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white text-xs focus:outline-none focus:border-teal-500 font-mono"
            />
          </div>

          {/* Watermark Style */}
          <div className="space-y-1">
            <label className="block text-xs font-semibold text-slate-300">
              Placement Style:
            </label>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <button
                type="button"
                onClick={() => setWatermarkStyle('diagonal_grid')}
                className={`py-1.5 rounded-lg font-medium transition-colors ${
                  watermarkStyle === 'diagonal_grid' ? 'bg-teal-600 text-white' : 'bg-slate-950 text-slate-400 border border-slate-800'
                }`}
              >
                Diagonal Grid
              </button>
              <button
                type="button"
                onClick={() => setWatermarkStyle('centre_stamp')}
                className={`py-1.5 rounded-lg font-medium transition-colors ${
                  watermarkStyle === 'centre_stamp' ? 'bg-teal-600 text-white' : 'bg-slate-950 text-slate-400 border border-slate-800'
                }`}
              >
                Centre Box
              </button>
              <button
                type="button"
                onClick={() => setWatermarkStyle('footer_banner')}
                className={`py-1.5 rounded-lg font-medium transition-colors ${
                  watermarkStyle === 'footer_banner' ? 'bg-teal-600 text-white' : 'bg-slate-950 text-slate-400 border border-slate-800'
                }`}
              >
                Footer Strip
              </button>
            </div>
          </div>

          {/* Color & Opacity Controls */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="space-y-1">
              <label className="block text-slate-300 font-semibold">Color:</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setColorTheme('red')}
                  className={`w-6 h-6 rounded-full bg-red-600 border-2 ${colorTheme === 'red' ? 'border-white' : 'border-transparent'}`}
                />
                <button
                  type="button"
                  onClick={() => setColorTheme('blue')}
                  className={`w-6 h-6 rounded-full bg-blue-600 border-2 ${colorTheme === 'blue' ? 'border-white' : 'border-transparent'}`}
                />
                <button
                  type="button"
                  onClick={() => setColorTheme('black')}
                  className={`w-6 h-6 rounded-full bg-slate-900 border-2 ${colorTheme === 'black' ? 'border-white' : 'border-transparent'}`}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="block text-slate-300 font-semibold">Opacity ({Math.round(opacity * 100)}%):</label>
              <input
                type="range"
                min="0.15"
                max="0.85"
                step="0.05"
                value={opacity}
                onChange={(e) => setOpacity(parseFloat(e.target.value))}
                className="w-full accent-teal-500"
              />
            </div>
          </div>
        </div>

        {/* Right Column: Live Canvas Preview & Download */}
        <div className="lg:col-span-7 space-y-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-teal-400" />
                <h4 className="text-sm font-bold text-white">Live Watermarked Preview</h4>
              </div>
              <button
                onClick={handleDownload}
                className="px-4 py-2 rounded-xl bg-teal-600 hover:bg-teal-500 text-white font-bold text-xs shadow-md transition-colors flex items-center gap-1.5"
              >
                <Download className="w-4 h-4" />
                <span>Download Watermarked Copy</span>
              </button>
            </div>

            {/* Canvas Area */}
            <div className="flex items-center justify-center p-4 bg-slate-950 rounded-2xl border border-slate-800/80 overflow-hidden watermark-container">
              <canvas ref={canvasRef} className="max-w-full h-auto rounded-lg shadow-2xl" />
            </div>

            <p className="text-[11px] text-slate-400 text-center">
              Generated watermark: <span className="font-mono text-teal-400">COPY — FOR IDENTITY VERIFICATION ONLY • {recipientCompany.toUpperCase()} • {watermarkDate}</span>
            </p>
          </div>

          {/* Section 15 Requirement Disclaimer */}
          <div className="p-4 rounded-2xl bg-amber-950/30 border border-amber-800/50 flex items-start gap-3 text-xs text-amber-200">
            <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-bold text-amber-300">Important Legal Guidance:</p>
              <p className="leading-relaxed text-slate-300">
                Watermarking does not guarantee protection against all misuse and should not obscure vital information (such as your name or card number) required for legitimate Document Verification Service (DVS) validation by authorized Australian entities.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
